import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { Users, Check, ChevronRight } from "lucide-react";

import { furnitureTypesObject, photoUrl } from "../../config";
import { Button } from "../ui/button";
import { declOfNum } from "../../lib/utils";
import RoomPhotoSlider from "./RoomPhotoSlider";
import DesignPreviewScroller from "./DesignPreviewScroller";
import PrintTariffPaymentConditions from "./PrintTariffPaymentConditions";

export default function RoomCardHorizontal({
  idx,
  room,
  roomPhotoUrl,
  photoIndex,
  slideDirection,
  onPrev,
  onNext,
  onPhotoClick,
  // expandedFeatures,
  // onToggleFeatures,
  onShowDescription,
  onBooking,
  onBook,
  datesSelected,
  onDesignVariants,
  accentColor = "#14b8a6",
  textColor,
}) {
  const [tariffsOpen, setTariffsOpen] = useState(false);
  const [photos, setPhotos] = useState([{ src: "/empty.jpg" }]);

  useEffect(() => {
    if (room.photos?.length > 0) {
      setPhotos(
        room.photos.map((p) => ({
          src: `${photoUrl}/${roomPhotoUrl}/${room.id}/${p}.jpg`,
          name: p,
        })),
      );
    }
  }, []);

  const bookingHandler = (e) => {
    onBooking(
      room.hotel,
      room.id,
      e.target.dataset.tariff,
      e.target.dataset.food,
    );
  };

  const tariffScrollRef = useRef(null);

  const handleTariffWheel = useCallback((e) => {
    if (!tariffScrollRef.current) return;
    e.preventDefault();
    tariffScrollRef.current.scrollLeft += e.deltaY;
  }, []);

  useEffect(() => {
    const el = tariffScrollRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleTariffWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleTariffWheel);
  }, [handleTariffWheel]);

  return (
    <motion.div
      key={room.id}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: idx * 0.1 }}
      className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden"
    >
      <div
        className="flex flex-col sm:flex-row sm:gap-0"
        style={{ minHeight: "240px" }}
      >
        {/* Image */}
        <div
          className={`flex flex-col flex-shrink-0 ${room?.separate ? "sm:w-[345px]" : "sm:w-[345px] sm:self-stretch"}`}
        >
          <RoomPhotoSlider
            images={photos}
            positions={{
              ...(room?.photos_positions || {}),
            }}
            currentIndex={photoIndex}
            direction={slideDirection}
            onPrev={onPrev}
            onNext={onNext}
            onPhotoClick={onPhotoClick}
            className="sm:rounded-tl-xl"
            style={
              room?.separate
                ? { height: 178 }
                : { height: "100%", minHeight: "240px" }
            }
          >
            <div className="absolute top-2 left-2 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm z-10">
              {room.hotels_space} м²
            </div>
          </RoomPhotoSlider>
          {room?.separate && (
            <div
              className="px-3 py-1.5 bg-gray-50 border-t border-gray-100 sm:rounded-bl-xl cursor-pointer hover:bg-teal-50 transition-colors"
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = `${accentColor}18`)
              }
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
              onClick={onDesignVariants}
            >
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Варианты дизайна
                </p>
                <span
                  className="text-xs text-teal-500 font-medium"
                  style={{ color: accentColor }}
                >
                  Посмотреть →
                </span>
              </div>
              <DesignPreviewScroller
                imgClassName="w-[77px] h-[53px] rounded-md"
                gap="gap-1"
                room={room}
                roomPhotoUrl={roomPhotoUrl}
                accentColor={accentColor}
                textColor={textColor}
              />
            </div>
          )}
        </div>

        {/* Content area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header — always visible */}
          <div className="flex items-start gap-3 mb-1.5 flex-shrink-0 px-5 pt-5">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-gray-900 leading-tight">
                {room.name}
              </h3>
              {room?.beds && (
                <p className="text-gray-600 text-sm mt-1">{room?.beds || ""}</p>
              )}
              {room.additional_terms?.place_types?.length > 0 && (
                <p className="text-gray-500 text-sm mb-4">
                  +
                  {declOfNum(room.additional_terms.place_types.length, [
                    "доп.место",
                    "доп.места",
                    "доп.мест",
                  ])}{" "}
                  –{" "}
                  {[...new Set(room.additional_terms.place_types)]
                    .map((type) => furnitureTypesObject[type].toLowerCase())
                    .filter(Boolean)
                    .join(", ")}
                </p>
              )}
            </div>
            {/* Capacity + toggle buttons */}
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              {room?.room_capacity && (
                <div className="flex items-center gap-1 text-gray-500 text-sm">
                  <Users className="w-3.5 h-3.5" />
                  <span>
                    До{" "}
                    {declOfNum(
                      room.room_capacity +
                        (room?.additional_terms?.places?.length || 0),
                      ["гостя", "гостей", "гостей"],
                    )}
                  </span>
                </div>
              )}

              {room.price_variants_counter > 1 && (
                <button
                  onClick={() => setTariffsOpen((v) => !v)}
                  className="flex items-center gap-1 text-black-600 text-sm font-medium transition-colors whitespace-nowrap"
                  // style={{ color: textColor }}
                >
                  {tariffsOpen ? (
                    <>
                      <ChevronRight className="w-4 h-4 rotate-180" />
                      Скрыть
                    </>
                  ) : (
                    <>
                      Ещё{" "}
                      {declOfNum(room.price_variants_counter - 1, [
                        "вариант",
                        "варианта",
                        "вариантов",
                      ])}{" "}
                      цены
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Sliding body — features+booking or tariffs */}
          <div className="flex flex-1 min-w-0 overflow-hidden relative pb-0">
            {/* Main content: features + booking */}
            <motion.div
              animate={{ x: tariffsOpen ? "-100%" : "0%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="flex flex-1 min-w-0 flex-shrink-0 w-full"
            >
              <div className="flex flex-1 min-w-0 pl-5 pb-5">
                {/* Features — 2 columns */}
                <div className="flex flex-col flex-1 min-w-0">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-3 gap-y-1.5 mb-3 flex-1">
                    {(room?.services || []).map((feature, i) => (
                      <div
                        key={i}
                        className={`flex items-center gap-1.5 text-sm text-gray-600 ${i >= 4 ? "hidden lg:flex" : ""}`}
                      >
                        <Check
                          className="w-3.5 h-3.5 text-teal-600 flex-shrink-0"
                          style={{ color: textColor }}
                        />
                        <span className="truncate">{feature}</span>
                      </div>
                    ))}
                    {(room?.services || []).length > 4 && (
                      <div
                        className="lg:hidden flex items-center gap-1.5 text-sm text-teal-600 font-medium"
                        style={{ color: textColor }}
                      >
                        +{(room?.services || []).length - 4} удобств
                      </div>
                    )}
                  </div>
                  {/* text-teal-600 hover:text-teal-700 */}
                  <button
                    onClick={() => onShowDescription(room)}
                    className="text-sm text-black font-medium underline text-left mt-auto"
                  >
                    Подробное описание номера
                  </button>
                </div>

                {/* Vertical divider */}
                <div className="w-px bg-gray-200 flex-shrink-0" />

                {/* Right: price + actions */}
                <div className="flex flex-col gap-3 pl-5 pr-5 flex-shrink-0 w-[210px]">
                  <div className="flex-1" />
                  {room?.tariffs?.prices?.[0]?.total_cost ? (
                    <>
                      <div>
                        <div className="text-sm text-gray-500 mb-1 firstUp">
                          {room.tariffs.prices[0].food_type}
                        </div>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-xl font-bold text-gray-900">
                            {room.tariffs.prices[0].total_cost} ₽
                          </span>
                          <span className="text-sm text-gray-500">
                            за {room.tariffs.days_count}{" "}
                            {room.tariffs.days_count === 1
                              ? "ночь"
                              : room.tariffs.days_count < 5
                                ? "ночи"
                                : "ночей"}
                          </span>
                        </div>
                      </div>
                      <Button
                        onClick={bookingHandler}
                        data-food={room.tariffs.prices[0].food_type}
                        data-tariff="base"
                        className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white text-sm h-9 w-full"
                        style={{
                          background: `linear-gradient(to right, ${accentColor}, ${accentColor}cc)`,
                          color: textColor,
                        }}
                      >
                        Забронировать
                      </Button>
                    </>
                  ) : (
                    !datesSelected &&
                    room?.available_periods && (
                      <Button
                        onClick={() => onBook(room)}
                        className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white text-sm h-9 w-full"
                        style={{
                          background: `linear-gradient(to right, ${accentColor}, ${accentColor}cc)`,
                          color: textColor,
                        }}
                      >
                        Свободные даты
                      </Button>
                    )
                  )}
                </div>
              </div>
            </motion.div>

            {/* Tariff panel — slides in from right */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: tariffsOpen ? "0%" : "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="absolute inset-0 bg-white z-20 flex flex-col"
            >
              {/* All tariff items as columns */}
              <div
                ref={tariffScrollRef}
                className="flex flex-1 min-w-0 overflow-x-auto divide-x divide-gray-100"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {room.tariffs?.variants?.length > 0 &&
                  room.tariffs.variants.map((variant, groupIndex) => {
                    if (!variant.prices?.length) {
                      return null;
                    }
                    const isFirstGroup = groupIndex === 0;

                    return variant.prices.map((item, itemIndex) => {
                      const price = item.total_cost;
                      const isFirstItem = itemIndex === 0;

                      return (
                        <div
                          key={`${variant.id}-${itemIndex}`}
                          className={`flex flex-col flex-1 min-w-[220px] ${isFirstGroup ? "bg-red-50" : ""}`}
                        >
                          {/* Note header only for first item in first group */}
                          {isFirstGroup && isFirstItem && (
                            <div className="px-4 py-1.5 border-b border-red-100 flex-shrink-0">
                              <div className="text-xs text-red-500">
                                <PrintTariffPaymentConditions
                                  tariff={variant}
                                />
                              </div>
                            </div>
                          )}
                          <div className="flex flex-col flex-1 px-4 pt-3 pb-5">
                            <div className="flex-1" />
                            <div className="text-sm text-gray-500 mb-0.5 whitespace-nowrap">
                              {item.food_type}
                            </div>
                            <div className="flex items-baseline gap-1.5 whitespace-nowrap mb-3">
                              <span className="text-base font-bold text-gray-900">
                                {price.toLocaleString("ru-RU")} ₽
                              </span>
                              {room.tariffs.days_count > 0 && (
                                <span className="text-xs text-gray-500">
                                  за {room.tariffs.days_count}{" "}
                                  {room.tariffs.days_count === 1
                                    ? "ночь"
                                    : room.tariffs.days_count < 5
                                      ? "ночи"
                                      : "ночей"}
                                </span>
                              )}
                            </div>
                            <Button
                              data-tariff={variant.id}
                              data-food={item.food_type}
                              onClick={bookingHandler}
                              className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white text-sm h-9 px-5"
                              style={{
                                background: `linear-gradient(to right, ${accentColor}, ${accentColor}cc)`,
                                color: textColor,
                              }}
                            >
                              Забронировать
                            </Button>
                          </div>
                        </div>
                      );
                    });
                  })}

                {/* Базовый тариф */}
                {room.tariffs?.prices?.length > 0 &&
                  room.tariffs.prices.map((item, itemIndex) => {
                    const price = item.total_cost;
                    const isFirstItem = itemIndex === 0;

                    return (
                      <div
                        key={`base-${itemIndex}`}
                        className="flex flex-col flex-1 min-w-[220px]"
                      >
                        {/* Note header only for first item */}
                        {isFirstItem && (
                          <div className="px-4 py-1.5 border-b border-gray-100 flex-shrink-0">
                            <div className="text-xs text-gray-500">
                              <PrintTariffPaymentConditions
                                tariff={room.tariffs}
                              />
                            </div>
                          </div>
                        )}
                        <div className="flex flex-col flex-1 px-4 pt-3 pb-5">
                          <div className="flex-1" />
                          <div className="text-sm text-gray-500 mb-0.5 whitespace-nowrap">
                            {item.food_type}
                          </div>
                          <div className="flex items-baseline gap-1.5 whitespace-nowrap mb-3">
                            <span className="text-base font-bold text-gray-900">
                              {price.toLocaleString("ru-RU")} ₽
                            </span>
                            {room.tariffs.days_count > 0 && (
                              <span className="text-xs text-gray-500">
                                за {room.tariffs.days_count}{" "}
                                {room.tariffs.days_count === 1
                                  ? "ночь"
                                  : room.tariffs.days_count < 5
                                    ? "ночи"
                                    : "ночей"}
                              </span>
                            )}
                          </div>
                          <Button
                            onClick={bookingHandler}
                            data-tariff="base"
                            data-food={item.food_type}
                            className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white text-sm h-9 px-5"
                            style={{
                              background: `linear-gradient(to right, ${accentColor}, ${accentColor}cc)`,
                              color: textColor,
                            }}
                          >
                            Забронировать
                          </Button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
